import { useCallback, useMemo } from 'react';
import type { DataRoomFile } from '@/types/documents/documents.types';
import type { Person, Document } from '@/components/common/Ask/EnhancedTextArea/types';
import type { TransformedUserWorkspace } from '@/utils/data/transformWorkSpaceItems';
import { MODIFIER_KEYS } from './AskSession.constants';
import { getFileExtension } from './AskSession.utils';

export type UseAskSessionRenderHelpersParams = {
  isMac: boolean;
  workspaceData: TransformedUserWorkspace | null;
};

export type UseAskSessionRenderHelpersResult = {
  getModifierKey: () => string;
  preparedDocuments: Document[];
  preparedPeople: Person[];
};

export const useAskSessionRenderHelpers = ({
  isMac,
  workspaceData,
}: UseAskSessionRenderHelpersParams): UseAskSessionRenderHelpersResult => {
  const getModifierKey = useCallback(
    () => (isMac ? MODIFIER_KEYS.CMD : MODIFIER_KEYS.CTRL),
    [isMac],
  );

  const preparedDocuments = useMemo(() => {
    return (
      workspaceData?.dataRoom?.workspaceDataRoomFiles?.items
        ?.filter((item): item is DataRoomFile => {
          const file = item as DataRoomFile;
          return (
            file &&
            typeof file === 'object' &&
            'dataRoomFileId' in file &&
            !file.markedForDeletion
          );
        })
        ?.map((doc) => {
          const fileExtension = getFileExtension(doc.name);
          return {
            id: doc.dataRoomFileId,
            name: doc.name || 'Untitled Document',
            type: fileExtension.toLowerCase(),
            size: doc.readableSize || 'Unknown size',
            category: 'documents',
            dataRoomFileId: doc.dataRoomFileId,
          };
        }) || []
    );
  }, [workspaceData]);

  const preparedPeople = useMemo(() => {
    return workspaceData?.workspaceUsers?.userMetadata
      ? Object.entries(workspaceData.workspaceUsers.userMetadata).map(
          ([userId, metadata]) => ({
            id: userId,
            name:
              workspaceData.workspaceUsers.userData[userId].firstName +
              ' ' +
              workspaceData.workspaceUsers.userData[userId].lastName,
            type: 'person' as const,
            userId: userId,
            image: metadata.image,
            isOwner: metadata.isOwner,
          }),
        )
      : [];
  }, [workspaceData]);

  return {
    getModifierKey,
    preparedDocuments,
    preparedPeople,
  };
};

